import React, { useState, useRef } from "react";
import { Form, InputGroup } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import Preloader from "../shared/PreLoaders";
const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const Api = process.env.REACT_APP_API;
  const inputRefs = useRef([]);
  const [otpArray, setOtpArray] = useState(Array(6).fill(""));

  // ✅ Password validation function
  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
    return regex.test(password);
  };

  // ✅ Request OTP
  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (!email || !username) {
      setError("Please enter both email and username.");
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${Api}/api/auth/resend-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, username }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || "Failed to send OTP. Please check your details."
        );
      }

      setOtpSent(true);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const otp = otpArray.join("");

    if (otp.length !== 6) {
      setError("Please enter the 6-digit OTP.");
      setLoading(false);
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError("Please fill all required fields.");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      setLoading(false);
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters long and include uppercase, lowercase, number, and special character."
      );
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${Api}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, username, otp, newPassword }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      navigate("/login-1");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };


  const handleOtpChange = (value, index) => {
    if (/^\d$/.test(value)) {
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = value;
      setOtpArray(updatedOtpArray);

      // Move focus to the next input if available
      if (index < otpArray.length - 1) {
        inputRefs.current[index + 1].focus();
      }
    } else if (value === "") {
      // Handle backspace
      const updatedOtpArray = [...otpArray];
      updatedOtpArray[index] = "";
      setOtpArray(updatedOtpArray);

      // Move focus to the previous input if available
      if (index > 0) {
        inputRefs.current[index - 1].focus();
      }
    }
  };


  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpArray[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Handle Paste Event
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    if (/^\d{6}$/.test(pastedData)) {
      const updatedOtpArray = pastedData.split("");
      setOtpArray(updatedOtpArray);

      // Move focus to the last field
      inputRefs.current[5]?.focus();
    }
  };

  const passwordsMatch =
    newPassword && confirmPassword && newPassword === confirmPassword;

  const isPasswordStrong = validatePassword(newPassword);

  const isFormValid =
    otpSent &&
    otpArray.join("").length === 6 &&
    passwordsMatch &&
    isPasswordStrong &&
    !loading;


  return (
    <div>
      <div className="login-wrapper">

        <div className="login-card"><div className="login-card">

          {/* LEFT */}
          <div className="login-form-section">
            <div className="logo-block">
              <img src={require("../../assets/images/logo_dark.png")} alt="TNFD" />

              <h3 className="mt-5">Joint Forest Management Committe</h3>
            </div>

            {/* <p className="subtitle">
                    A new day in nature begins. It's your turn to act.
                  </p> */}

            <h4 className="signin-title mt-5">
              {otpSent ? "VERIFY OTP" : "SIGN IN"}
            </h4>
            <h6 className="font-weight-light mb-4 text-center">
              {otpSent
                ? "Enter OTP and set a new password."
                : "Enter your username and email to receive OTP."}
            </h6>
            {error && <p className="error">{error}</p>}

            <form
              className="pt-3"
              onSubmit={otpSent ? handleResetPassword : handleRequestOTP}
            >
              {error && (
                <p className="text-danger fw-bold mb-3">
                  <i className="mdi mdi-alert-circle-outline"></i> {error}
                </p>
              )}

              {/* Step 1: Username + Email */}
              {!otpSent && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="text"
                      placeholder="Username"
                      size="lg"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Control
                      type="email"
                      placeholder="Email"
                      size="lg"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </Form.Group>
                </>
              )}

              {/* Step 2: OTP + Password */}
              {otpSent && (
                <>
                  <div className="otp-input mb-3">
                    {otpArray.map((digit, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        type="text"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, index)}
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        className="otp-box"
                        required
                      />
                    ))}
                  </div>


                  <Form.Group className="mb-3 position-relative">
                    <InputGroup>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        placeholder="New Password"
                        size="lg"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                      />
                      <InputGroup.Text
                        onClick={() => setShowPassword(!showPassword)}
                        style={{
                          cursor: "pointer",
                          background: "transparent",
                          borderLeft: "none",
                        }}
                      >
                        {showPassword ? (
                          <FaEyeSlash size={18} />
                        ) : (
                          <FaEye size={18} />
                        )}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>

                  <Form.Group className="mb-3 position-relative">
                    <InputGroup>
                      <Form.Control
                        type={showConfirm ? "text" : "password"}
                        placeholder="Confirm Password"
                        size="lg"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                      />
                      <InputGroup.Text
                        onClick={() => setShowConfirm(!showConfirm)}
                        style={{
                          cursor: "pointer",
                          background: "transparent",
                          borderLeft: "none",
                        }}
                      >
                        {showConfirm ? (
                          <FaEyeSlash size={18} />
                        ) : (
                          <FaEye size={18} />
                        )}
                      </InputGroup.Text>
                    </InputGroup>
                  </Form.Group>
                  {confirmPassword && !passwordsMatch && (
                    <p className="text-danger mt-1 mb-0">
                      Passwords do not match
                    </p>
                  )}

                  {/* Password strength hint */}
                  {newPassword && !validatePassword(newPassword) && (
                    <ul
                      style={{
                        color: "red",
                        marginTop: "5px",
                        marginBottom: 0,
                      }}
                    >
                      <li>At least 8 characters long</li>
                      <li>Must include an uppercase letter (A–Z)</li>
                      <li>Must include a lowercase letter (a–z)</li>
                      <li>Must include a number (0–9)</li>
                      <li>
                        Must include a special character (!@#$%^&* etc.)
                      </li>
                    </ul>
                  )}
                </>
              )}

              <div className="jfmc-switch mt-4">
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={otpSent ? !isFormValid : loading}
                >
                  {loading ? (
                    <>
                      <Preloader loading={loading} /> Processing...
                    </>
                  ) : otpSent ? (
                    "Update Password"
                  ) : (
                    "Request OTP"
                  )}
                </button>
              </div>

              <div className="text-center mt-4">
                <Link to="/login-1" className="auth-link text-black">
                  Back to Login
                </Link>
              </div>
            </form>
          </div>

          {/* RIGHT */}
          <div className="login-image-section">
            <img src={require("../../assets/images/auth/forgot.jpg")} alt="Forest" />
          </div>

        </div></div>

      </div>
    </div>


  );
};

export default ForgotPassword;
